import React, {useEffect, useRef, useState} from 'react'
import {FormInputWrapper} from '@modifi/plugin-onboarding'
import {
  Row,
  Col,
  DangerAlert,
  TextAreaPure,
  withInputContainer,
  CustomerDialogButtonRow,
  H5,
  Paragraph,
  ButtonPrimary,
  AutoApprovedFilledIcon,
} from '@modifi/ui'
import {useTranslation} from '@modifi/plugin-i18n'
import {Form, FormActionsContext} from '@modifi/plugin-forms'
import {TaskStatus} from '@modifi/enums'
import {landGreen} from '@modifi/colors'

import {TaskUpdate} from '../../constants'

const TextAreaPureWithInputContainer = withInputContainer(TextAreaPure)

export type ResolveTaskDialogProps = {
  params: {
    taskId: string
    internalNote?: string
    category?: string
  }
  isUpdatePending?: boolean
  hasUpdateError?: boolean
  updateErrorMessage?: string
  submit: (taskId: string, changes: TaskUpdate, actionMessage?: string) => void
  reset: () => void
  discard: () => void
}

const ResolveTaskDialog: React.FC<ResolveTaskDialogProps> = ({
  params,
  isUpdatePending = false,
  hasUpdateError = false,
  updateErrorMessage = undefined,
  submit,
  discard,
  reset,
}) => {
  const {t} = useTranslation(['kartavya', 'plugin-forms'])
  const updateRef = useRef(isUpdatePending)
  const [showResolutionMessage, setShowResolutionMessage] = useState(false)

  useEffect(
    () => () => {
      reset()
    },
    [reset]
  )

  useEffect(() => {
    if (updateRef.current && !isUpdatePending && !hasUpdateError) {
      setShowResolutionMessage(true)
    }
    updateRef.current = isUpdatePending
  }, [hasUpdateError, isUpdatePending])

  const handleSubmit = (changes: {internalNote?: string}) => {
    submit(params.taskId, {status: TaskStatus.RESOLVED, internalNote: changes.internalNote})
  }

  if (showResolutionMessage) {
    return (
      <Row justifyContent="center">
        <Col>
          <Row justifyContent="center" mb={16}>
            <AutoApprovedFilledIcon color={landGreen} height={32} width={32} />
            <H5 mt={0} ml={8}>
              {t('tasks.resolve-task-dialog.resolved.headline')}
            </H5>
          </Row>
        </Col>
        <Col>
          <Paragraph textAlign="center">
            {t([
              `tasks.category-${params.category}.resolve-task-dialog.resolved.description`,
              'tasks.resolve-task-dialog.resolved.description',
            ])}
          </Paragraph>
        </Col>
        <Col auto mt={24}>
          <ButtonPrimary
            type="button"
            data-target="close-resolution-success-dialog"
            onClick={discard}
            px={80}
          >
            {t('tasks.resolve-task-dialog.resolved.cta-close')}
          </ButtonPrimary>
        </Col>
      </Row>
    )
  }

  return (
    <>
      {updateErrorMessage && (
        <Row mb={16}>
          <Col>
            <DangerAlert>{updateErrorMessage}</DangerAlert>
          </Col>
        </Row>
      )}
      <Row justifyContent="center" mb={24}>
        <Col>
          <H5 textAlign="center">{t('tasks.resolve-task-dialog.headline')}</H5>
        </Col>
        <Col auto>
          <Paragraph>{t('tasks.resolve-task-dialog.description')}</Paragraph>
        </Col>
      </Row>
      <Form
        initialValues={{internalNote: params.internalNote}}
        onSubmit={handleSubmit}
        initialFormIsValid
        isLoading={isUpdatePending}
      >
        <Row>
          <Col>
            <FormInputWrapper
              name="internalNote"
              inputComponent={TextAreaPureWithInputContainer}
              placeholder={t('tasks.resolve-task-dialog.internal-note.placeholder')}
              optional
              label={t('tasks.resolve-task-dialog.internal-note.label')}
              required={false}
              t={t}
              rows={5}
            />
          </Col>
        </Row>
        <FormActionsContext.Consumer>
          {ctx => (
            <CustomerDialogButtonRow
              canSubmit={ctx.canSubmit && !isUpdatePending}
              onSubmitClick={ctx.submit}
              submitText={t('tasks.resolve-task-dialog.cta-submit')}
              hasCancel
              canCancel
              cancelText={t('tasks.resolve-task-dialog.cta-cancel')}
              onCancelClick={discard}
              loading={isUpdatePending}
            />
          )}
        </FormActionsContext.Consumer>
      </Form>
    </>
  )
}

export default ResolveTaskDialog
